import { inject, Injectable } from '@angular/core';
import { AssetsService } from '@api';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { PropertiesOverview } from 'src/app/shared/components/properties-overview/properties-overview';
import '../../shared/extensions/string.extensions';
import { ApplicationState } from '../../shared/state/application.state';
import { ResetAssetDetails } from '../asset-details.actions';
import { assetPropertiesDefaults, AssetPropertiesStateModel } from '../asset-property-state/asset-properties.state.model';
import { AssetSelectedState } from '../asset-selected-state/asset-selected.state';
import { AssetProperty } from './../../shared/services/api/model/assetProperty';
import * as actions from './asset-properties.actions';

@State<AssetPropertiesStateModel>(assetPropertiesDefaults)
@Injectable()
export class AssetPropertiesState {
    private assetsService = inject(AssetsService);
    private store = inject(Store);

    @Selector()
    public static getPropertyValues(state: AssetPropertiesStateModel) {
        return state.propertyValues;
    }

    @Selector()
    public static getLastKnownTelemetryValues(state: AssetPropertiesStateModel) {
        return state.lastKnownTelemetryValues;
    }

    @Selector()
    public static getReportedPropertyValues(state: AssetPropertiesStateModel) {
        return PropertiesOverview.getFlattenedProperties(state.propertyValues);
    }

    @Selector()
    public static getReferencedEntities(state: AssetPropertiesStateModel) {
        return state.referencedEntities;
    }

    @Action(actions.GetPropertyValues)
    public getPropertyValues({ getState, setState }: StateContext<AssetPropertiesStateModel>, action: actions.GetPropertyValues) {
        const deviceId = this.store.selectSnapshot(AssetSelectedState.getSelectedDeviceId);
        const assetId = this.store.selectSnapshot(AssetSelectedState.getSelectedAssetId);
        const tenantName = this.store.selectSnapshot(ApplicationState.tenantName);

        return this.assetsService.getPropertyValues(deviceId, assetId, tenantName).pipe(
            tap((result) => {
                const state = getState();
                setState({
                    ...state,
                    propertyValues: result,
                });
            })
        );
    }

    @Action(actions.GetLastKnownAssetTelemetryValues)
    public getLastKnownAssetTelemetryValues({ getState, setState }: StateContext<AssetPropertiesStateModel>, action: actions.GetPropertyValues) {
        const deviceId = this.store.selectSnapshot(AssetSelectedState.getSelectedDeviceId);
        const assetId = this.store.selectSnapshot(AssetSelectedState.getSelectedAssetId);
        const tenantName = this.store.selectSnapshot(ApplicationState.tenantName);

        return this.assetsService.getLastKnownAssetTelemetryValues(deviceId, assetId, tenantName).pipe(
            tap((result) => {
                const state = getState();
                setState({
                    ...state,
                    lastKnownTelemetryValues: result,
                });
            })
        );
    }

    @Action(actions.SetPropertyValues)
    public setPropertyValues(ctx: StateContext<AssetPropertiesStateModel>, action: actions.SetPropertyValues) {
        const state = ctx.getState();
        const deviceId = this.store.selectSnapshot(AssetSelectedState.getSelectedDeviceId);
        const assetId = this.store.selectSnapshot(AssetSelectedState.getSelectedAssetId);
        const tenantName = this.store.selectSnapshot(ApplicationState.tenantName);

        return this.assetsService.setPropertyValues(deviceId, assetId, tenantName, action.properties, action.force).pipe(
            tap((result) => {
                this.saveProperties(state, ctx, result);
            }),
            catchError((err) => {
                if (err.status === 409) {
                    ctx.patchState({
                        referencedEntities: err.error.conflicts,
                    });

                    return of();
                }

                return throwError(err);
            })
        );
    }

    @Action(actions.PushProperty)
    public pushProperty({ getState, patchState }: StateContext<AssetPropertiesStateModel>, action: actions.PushProperty) {
        const state = getState();
        const propertyValues = state.propertyValues;
        if (action.data.length === 0 || propertyValues.length === 0) {
            return;
        }
        const changedPropertyValues: AssetProperty[] = [];

        for (const propertyValue of propertyValues) {
            const foundIndex = action.data.findIndex((entity) => entity.name === propertyValue.name);

            if (foundIndex == -1) {
                changedPropertyValues.push(propertyValue);
            } else {
                const property = action.data[foundIndex];
                const desiredValue = property.desiredValue ?? propertyValue.desiredValue;

                // update reportedValue only if its not an error
                const reportedValue = property.error != null ? propertyValue.reportedValue : property.reportedValue;

                // only delete error if reported and desired match
                const error = property.error == null && property.reportedValue == desiredValue ? null : (property.error ?? propertyValue.error);

                changedPropertyValues.push({
                    desiredValue: propertyValue.desiredValue,
                    name: propertyValue.name,
                    changedAt: propertyValue.changedAt,
                    reportedValue: reportedValue,
                    error: error,
                    reportedAt: property.reportedAt,
                });
            }
        }

        patchState({
            propertyValues: [...changedPropertyValues],
        });
    }

    @Action(ResetAssetDetails)
    public resetState(ctx: StateContext<AssetPropertiesStateModel>) {
        ctx.setState(assetPropertiesDefaults.defaults);
    }

    private saveProperties(state: AssetPropertiesStateModel, ctx: StateContext<AssetPropertiesStateModel>, result: AssetProperty[]) {
        const properties = state.propertyValues;
        const propertiesResult: AssetProperty[] = [];

        if (properties.length === 0) {
            ctx.patchState({ propertyValues: result });
        } else {
            let findProperty = false;
            for (const property of properties) {
                const updatedProperty = result?.find((entity) => entity.name === property.name);

                if (updatedProperty) {
                    findProperty = true;
                    propertiesResult.push(updatedProperty);
                } else {
                    propertiesResult.push(property);
                }
            }

            if (!findProperty) {
                for (const item of result) {
                    propertiesResult.push(item);
                }
                ctx.patchState({ propertyValues: propertiesResult });
            } else {
                ctx.patchState({ propertyValues: propertiesResult });
            }
        }
    }
}
